import {
  EXTENSAO_OPCOES, FORMAS, GRANDES_OPACIDADES, LADOS, LARGURA_OPCOES,
  LEGENDA_EXTENSAO, LEGENDA_LARGURA, LOCAIS_DIFUSO, LOCAIS_PLACA, LOCAL_OPCOES,
  PROFUSAO, QUALIDADE, SIMBOLOS, ZONAS, type DadosOit, type Lado,
} from '../../../compartilhado/oit.ts';
import '../estilos/oit.css';

/* A folha na tela é a mesma do papel: o médico clica na opção e um X aparece
   sobre ela. Nada de menus ou campos de texto no lugar das grades -- quem lê
   pneumoconiose há anos conhece este formulário de cor, e trocar o desenho por
   uma interface "melhor" custaria mais do que ganharia. */

function Caixa({
  marcado, aoClicar, texto, somenteLeitura,
}: { marcado: boolean; aoClicar: () => void; texto: string; somenteLeitura?: boolean }) {
  return (
    <button
      type="button"
      className="oit-caixa"
      onClick={aoClicar}
      disabled={somenteLeitura}
      aria-pressed={marcado}
    >
      <span className="oit-caixa__quadro" data-marcado={marcado || undefined} />
      <span className="oit-caixa__texto">{texto}</span>
    </button>
  );
}

function SimNao({
  valor, aoMudar, simRotulo, naoRotulo, somenteLeitura,
}: {
  valor: boolean | null;
  aoMudar: (v: boolean) => void;
  simRotulo: string;
  naoRotulo: string;
  somenteLeitura?: boolean;
}) {
  return (
    <div className="oit__opcoes">
      <Caixa marcado={valor === true} aoClicar={() => aoMudar(true)} texto={simRotulo} somenteLeitura={somenteLeitura} />
      <Caixa marcado={valor === false} aoClicar={() => aoMudar(false)} texto={naoRotulo} somenteLeitura={somenteLeitura} />
    </div>
  );
}

/* As grades sao <table> de verdade, e nao caixas com display:table: o
   formulario tem celula que ocupa duas colunas (o rotulo D ao lado do 0 da
   extensao) e, sem colspan, ela fica solta no meio da linha. */

function Celula({
  texto, marcada, aoClicar, rotulo, vazia, colunas, somenteLeitura,
}: {
  texto?: string;
  marcada?: boolean;
  aoClicar?: () => void;
  /** Celula de rotulo (D, E): tem a moldura do papel mas nao e opcao. */
  rotulo?: boolean;
  vazia?: boolean;
  colunas?: number;
  somenteLeitura?: boolean;
}) {
  const classe = 'oit-celula' + (vazia ? ' oit-celula--vazia' : '');
  if (rotulo) {
    return (
      <td className={classe} colSpan={colunas}>
        {texto}
      </td>
    );
  }
  return (
    <td className={classe} colSpan={colunas} data-marcada={marcada || undefined}>
      <button
        type="button"
        className="oit-celula__toque"
        onClick={aoClicar}
        disabled={somenteLeitura}
        aria-pressed={marcada}
        aria-label={texto || 'marcar'}
      >
        {texto ?? ' '}
      </button>
    </td>
  );
}

function Grade({
  titulo, compacta, children,
}: { titulo?: string; compacta?: boolean; children: React.ReactNode }) {
  return (
    <div className="oit-grade">
      {titulo && <span className="oit-grade__titulo">{titulo}</span>}
      <table className={'oit-grade__quadro' + (compacta ? ' oit-grade__quadro--compacta' : '')}>
        <tbody>{children}</tbody>
      </table>
    </div>
  );
}

export function FolhaOit({
  dados, aoMudar, cabecalho, assinatura, somenteLeitura,
}: {
  dados: DadosOit;
  aoMudar: (d: DadosOit) => void;
  cabecalho: {
    funcionario: string;
    empresa: string;
    dataRx: string;
    dataLeitura: string;
    clinica: string;
    /** Data URL da logo. No PDF ela vai embutida; na tela do médico não vai,
     *  e o nome em texto ocupa o lugar. */
    logo?: string | null;
    rodape: string;
  };
  assinatura?: { url: string | null; escala?: number; nome: string; crm: string };
  somenteLeitura?: boolean;
}) {
  const def = <C extends keyof DadosOit>(chave: C, valor: DadosOit[C]) =>
    aoMudar({ ...dados, [chave]: valor });

  /** Marcar de novo a mesma opção desmarca: é como o médico corrige sem
   *  precisar de um botão "limpar" para cada grade. */
  const alterna = <C extends keyof DadosOit>(chave: C, valor: string) =>
    def(chave, (dados[chave] === valor ? null : valor) as DadosOit[C]);

  const alternaEm = (
    chave: 'placasLocal' | 'placasCalcificacao' | 'difusoCalcificacao',
    local: string,
    valor: string,
  ) => def(chave, { ...dados[chave], [local]: dados[chave][local] === valor ? null : valor });

  const alternaLado = (
    chave: 'placasExtensao' | 'placasLargura' | 'difusoExtensao' | 'difusoLargura',
    lado: Lado,
    valor: string,
  ) => def(chave, { ...dados[chave], [lado]: dados[chave][lado] === valor ? null : valor });

  const alternaZona = (lado: Lado, i: number) => {
    const copia = [...dados.zonas[lado]];
    copia[i] = !copia[i];
    def('zonas', { ...dados.zonas, [lado]: copia });
  };

  const alternaSimbolo = (s: string) =>
    def('simbolos', dados.simbolos.includes(s)
      ? dados.simbolos.filter((x) => x !== s)
      : [...dados.simbolos, s]);

  const sl = somenteLeitura;

  return (
    <div className="oit">
      <header className="oit__cabecalho">
        {cabecalho.logo ? (
          <img className="oit__logo" src={cabecalho.logo} alt={cabecalho.clinica} />
        ) : (
          <span className="oit__marca">{cabecalho.clinica}</span>
        )}
        <span className="oit__titulo">
          FOLHA DE LEITURA RADIOLÓGICA - CLASSIFICAÇÃO INTERNACIONAL
          <br />
          DE RADIOGRAFIAS DE PNEUMOCONIOSE - OIT 2000
        </span>
      </header>

      <div className="oit__identificacao">
        <div className="oit__linha-id">
          <div className="oit__campo-id">
            <span className="oit__rotulo-id">Nome do funcionário:</span>
            <span className="oit__valor-id">{cabecalho.funcionario}</span>
          </div>
          <div className="oit__campo-id">
            <span className="oit__rotulo-id">Data do RX</span>
            <span className="oit__valor-id">{cabecalho.dataRx}</span>
          </div>
        </div>
        <div className="oit__linha-id">
          <div className="oit__campo-id">
            <span className="oit__rotulo-id">Nome da empresa:</span>
            <span className="oit__valor-id">{cabecalho.empresa}</span>
          </div>
          <div className="oit__campo-id">
            <span className="oit__rotulo-id">Data da leitura</span>
            <span className="oit__valor-id">{cabecalho.dataLeitura}</span>
          </div>
        </div>
      </div>

      {/* ------------------------------------------------------ 1A e 1B */}
      <div className="oit__par">
        <div className="oit__secao">
          <div className="oit__numero">1A</div>
          <div className="oit__corpo">
            <div className="oit__grupos">
              <span className="oit__enunciado">Qualidade técnica:</span>
              <Grade>
                <tr>
                  {QUALIDADE.map((q) => (
                    <Celula
                      key={q}
                      texto={q}
                      marcada={dados.qualidadeTecnica === q}
                      aoClicar={() => alterna('qualidadeTecnica', q)}
                      somenteLeitura={sl}
                    />
                  ))}
                </tr>
              </Grade>
            </div>
            <div style={{ marginTop: 4, display: 'flex', gap: 6, alignItems: 'center' }}>
              <span className="oit__enunciado" style={{ textTransform: 'none' }}>
                Comentário:
              </span>
              {sl ? (
                <span className="oit__texto" style={{ flex: 1 }}>{dados.qualidadeComentario}</span>
              ) : (
                <input
                  className="oit__texto"
                  style={{ flex: 1, border: '1px solid #000', font: 'inherit', padding: '1px 3px' }}
                  value={dados.qualidadeComentario}
                  onChange={(e) => def('qualidadeComentario', e.target.value)}
                />
              )}
            </div>
          </div>
        </div>

        <div className="oit__secao">
          <div className="oit__numero">1B</div>
          <div className="oit__corpo">
            <span className="oit__enunciado">Radiografia normal:</span>
            <div style={{ marginTop: 3 }}>
              <Caixa
                marcado={dados.radiografiaNormal === true}
                aoClicar={() => def('radiografiaNormal', dados.radiografiaNormal === true ? null : true)}
                texto="SIM (finalizar a leitura)"
                somenteLeitura={sl}
              />
              <br />
              <Caixa
                marcado={dados.radiografiaNormal === false}
                aoClicar={() => def('radiografiaNormal', dados.radiografiaNormal === false ? null : false)}
                texto="NÃO (passe p/ a seção 2)"
                somenteLeitura={sl}
              />
            </div>
          </div>
        </div>
      </div>

      {/* ---------------------------------------------------------- 2A */}
      <div className="oit__secao">
        <div className="oit__numero">2A</div>
        <div className="oit__corpo oit__pergunta">
          <span className="oit__enunciado">
            Alguma anormalidade de parênquima
            <br />
            consistente com pneumoconiose:
          </span>
          <SimNao
            valor={dados.parenquimaAnormal}
            aoMudar={(v) => def('parenquimaAnormal', v)}
            simRotulo="SIM (complete 2B e 2C)"
            naoRotulo="NÃO (passe p/ a seção 4)"
            somenteLeitura={sl}
          />
        </div>
      </div>

      {/* ----------------------------------------------------- 2B e 2C */}
      <div className="oit__par">
        <div className="oit__secao">
          <div className="oit__numero">2B</div>
          <div className="oit__corpo">
            <span className="oit__enunciado">Pequenas opacidades:</span>
            <div className="oit__grupos" style={{ marginTop: 4 }}>
              <div>
                <span className="oit-grade__titulo" style={{ textAlign: 'left' }}>
                  a) Forma / tamanho
                </span>
                <div className="oit__grupos">
                  <Grade titulo="Primária">
                    {FORMAS.map((linha, i) => (
                      <tr key={i}>
                        {linha.map((f) => (
                          <Celula
                            key={f}
                            texto={f}
                            marcada={dados.formaPrimaria === f}
                            aoClicar={() => alterna('formaPrimaria', f)}
                            somenteLeitura={sl}
                          />
                        ))}
                      </tr>
                    ))}
                  </Grade>
                  <Grade titulo="Secundária">
                    {FORMAS.map((linha, i) => (
                      <tr key={i}>
                        {linha.map((f) => (
                          <Celula
                            key={f}
                            texto={f}
                            marcada={dados.formaSecundaria === f}
                            aoClicar={() => alterna('formaSecundaria', f)}
                            somenteLeitura={sl}
                          />
                        ))}
                      </tr>
                    ))}
                  </Grade>
                </div>
              </div>

              <div>
                <span className="oit-grade__titulo" style={{ textAlign: 'left' }}>b) Zonas</span>
                <Grade>
                  <tr>
                    {LADOS.map((l) => (
                      <Celula key={l} texto={l} rotulo vazia />
                    ))}
                  </tr>
                  {ZONAS.map((_, i) => (
                    <tr key={i}>
                      {LADOS.map((lado) => (
                        <Celula
                          key={lado}
                          vazia
                          marcada={dados.zonas[lado][i]}
                          aoClicar={() => alternaZona(lado, i)}
                          somenteLeitura={sl}
                        />
                      ))}
                    </tr>
                  ))}
                </Grade>
              </div>

              <div>
                <span className="oit-grade__titulo" style={{ textAlign: 'left' }}>c) Profusão</span>
                <Grade>
                  {PROFUSAO.map((linha, i) => (
                    <tr key={i}>
                      {linha.map((p) => (
                        <Celula
                          key={p}
                          texto={p}
                          marcada={dados.profusao === p}
                          aoClicar={() => alterna('profusao', p)}
                          somenteLeitura={sl}
                        />
                      ))}
                    </tr>
                  ))}
                </Grade>
              </div>
            </div>
          </div>
        </div>

        <div className="oit__secao">
          <div className="oit__numero">2C</div>
          <div className="oit__corpo">
            <span className="oit__enunciado">Grandes opacidades:</span>
            <div style={{ marginTop: 10 }}>
              <Grade>
                <tr>
                  {GRANDES_OPACIDADES.map((g) => (
                    <Celula
                      key={g}
                      texto={g}
                      marcada={dados.grandesOpacidades === g}
                      aoClicar={() => alterna('grandesOpacidades', g)}
                      somenteLeitura={sl}
                    />
                  ))}
                </tr>
              </Grade>
            </div>
          </div>
        </div>
      </div>

      {/* ---------------------------------------------------------- 3A */}
      <div className="oit__secao">
        <div className="oit__numero">3A</div>
        <div className="oit__corpo oit__pergunta">
          <span className="oit__enunciado">
            Alguma anormalidade pleural
            <br />
            consistente com pneumoconiose:
          </span>
          <SimNao
            valor={dados.pleuralAnormal}
            aoMudar={(v) => def('pleuralAnormal', v)}
            simRotulo="SIM (complete 3B, 3C e 3D)"
            naoRotulo="NÃO (passe p/ a seção 4)"
            somenteLeitura={sl}
          />
        </div>
      </div>

      {/* ---------------------------------------------------------- 3B */}
      <div className="oit__secao">
        <div className="oit__numero">3B</div>
        <div className="oit__corpo">
          <div className="oit__grupos" style={{ alignItems: 'center' }}>
            <span className="oit__enunciado">Placas pleurais:</span>
            <SimNao
              valor={dados.placas}
              aoMudar={(v) => def('placas', v)}
              simRotulo="SIM"
              naoRotulo="NÃO"
              somenteLeitura={sl}
            />
          </div>

          <div className="oit__grupos" style={{ marginTop: 6 }}>
            <Grade titulo="LOCAL">
              {LOCAIS_PLACA.map((l) => (
                <tr key={l.chave}>
                  <td className="oit__rotulo-linha">{l.rotulo}</td>
                  {LOCAL_OPCOES.map((o) => (
                    <Celula
                      key={o}
                      texto={o}
                      marcada={dados.placasLocal[l.chave] === o}
                      aoClicar={() => alternaEm('placasLocal', l.chave, o)}
                      somenteLeitura={sl}
                    />
                  ))}
                </tr>
              ))}
            </Grade>

            <Grade titulo="CALCIFICAÇÃO">
              {LOCAIS_PLACA.map((l) => (
                <tr key={l.chave}>
                  {LOCAL_OPCOES.map((o) => (
                    <Celula
                      key={o}
                      texto={o}
                      marcada={dados.placasCalcificacao[l.chave] === o}
                      aoClicar={() => alternaEm('placasCalcificacao', l.chave, o)}
                      somenteLeitura={sl}
                    />
                  ))}
                </tr>
              ))}
            </Grade>

            <div>
              <span className="oit-grade__titulo" style={{ textAlign: 'left' }}>
                EXTENSÃO: PAREDE (Combinado
                <br />
                em perfil e frontal)
              </span>
              <div className="oit__grupos">
                {LADOS.map((lado) => (
                  <Grade key={lado}>
                    <tr>
                      <Celula
                        texto="0"
                        marcada={dados.placasExtensao[lado] === '0'}
                        aoClicar={() => alternaLado('placasExtensao', lado, '0')}
                        somenteLeitura={sl}
                      />
                      <Celula texto={lado} rotulo colunas={2} />
                    </tr>
                    <tr>
                      {EXTENSAO_OPCOES.filter((o) => o !== '0').map((o) => (
                        <Celula
                          key={o}
                          texto={o}
                          marcada={dados.placasExtensao[lado] === o}
                          aoClicar={() => alternaLado('placasExtensao', lado, o)}
                          somenteLeitura={sl}
                        />
                      ))}
                    </tr>
                  </Grade>
                ))}
              </div>
              <p className="oit__legenda">
                {LEGENDA_EXTENSAO.map((t) => (
                  <span key={t}>{t}</span>
                ))}
              </p>
            </div>

            <div>
              <span className="oit-grade__titulo" style={{ textAlign: 'left' }}>
                LARGURA (OPCIONAL)
                <br />
                (Mínimo de 3mm para marcação)
              </span>
              <div className="oit__grupos">
                {LADOS.map((lado) => (
                  <Grade key={lado}>
                    <tr>
                      <Celula texto={lado} rotulo colunas={3} />
                    </tr>
                    <tr>
                      {LARGURA_OPCOES.map((o) => (
                        <Celula
                          key={o}
                          texto={o}
                          marcada={dados.placasLargura[lado] === o}
                          aoClicar={() => alternaLado('placasLargura', lado, o)}
                          somenteLeitura={sl}
                        />
                      ))}
                    </tr>
                  </Grade>
                ))}
              </div>
              <p className="oit__legenda">
                {LEGENDA_LARGURA.map((t) => (
                  <span key={t}>{t}</span>
                ))}
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* ---------------------------------------------------------- 3C */}
      <div className="oit__secao">
        <div className="oit__numero">3C</div>
        <div className="oit__corpo oit__grupos" style={{ alignItems: 'center' }}>
          <span className="oit__enunciado">Obliteração do seio costofrênico:</span>
          <Grade>
            <tr>
              {LOCAL_OPCOES.map((o) => (
                <Celula
                  key={o}
                  texto={o}
                  marcada={dados.obliteracaoSeio === o}
                  aoClicar={() => alterna('obliteracaoSeio', o)}
                  somenteLeitura={sl}
                />
              ))}
            </tr>
          </Grade>
        </div>
      </div>

      {/* ---------------------------------------------------------- 3D */}
      <div className="oit__secao">
        <div className="oit__numero">3D</div>
        <div className="oit__corpo">
          <div className="oit__grupos" style={{ alignItems: 'center' }}>
            <span className="oit__enunciado">Espessamento pleural difuso:</span>
            <SimNao
              valor={dados.difuso}
              aoMudar={(v) => def('difuso', v)}
              simRotulo="SIM"
              naoRotulo="NÃO"
              somenteLeitura={sl}
            />
          </div>

          <div className="oit__grupos" style={{ marginTop: 6 }}>
            <Grade titulo="CALCIFICAÇÃO">
              {LOCAIS_DIFUSO.map((l) => (
                <tr key={l.chave}>
                  <td className="oit__rotulo-linha">{l.rotulo}</td>
                  {LOCAL_OPCOES.map((o) => (
                    <Celula
                      key={o}
                      texto={o}
                      marcada={dados.difusoCalcificacao[l.chave] === o}
                      aoClicar={() => alternaEm('difusoCalcificacao', l.chave, o)}
                      somenteLeitura={sl}
                    />
                  ))}
                </tr>
              ))}
            </Grade>

            <div>
              <span className="oit-grade__titulo" style={{ textAlign: 'left' }}>
                EXTENSÃO: PAREDE (Combinado
                <br />
                em perfil e frontal)
              </span>
              <div className="oit__grupos">
                {LADOS.map((lado) => (
                  <Grade key={lado}>
                    <tr>
                      <Celula
                        texto="0"
                        marcada={dados.difusoExtensao[lado] === '0'}
                        aoClicar={() => alternaLado('difusoExtensao', lado, '0')}
                        somenteLeitura={sl}
                      />
                      <Celula texto={lado} rotulo colunas={2} />
                    </tr>
                    <tr>
                      {EXTENSAO_OPCOES.filter((o) => o !== '0').map((o) => (
                        <Celula
                          key={o}
                          texto={o}
                          marcada={dados.difusoExtensao[lado] === o}
                          aoClicar={() => alternaLado('difusoExtensao', lado, o)}
                          somenteLeitura={sl}
                        />
                      ))}
                    </tr>
                  </Grade>
                ))}
              </div>
              <p className="oit__legenda">
                {LEGENDA_EXTENSAO.map((t) => (
                  <span key={t}>{t}</span>
                ))}
              </p>
            </div>

            <div>
              <span className="oit-grade__titulo" style={{ textAlign: 'left' }}>
                LARGURA (OPCIONAL)
                <br />
                (Mínimo de 3mm para marcação)
              </span>
              <div className="oit__grupos">
                {LADOS.map((lado) => (
                  <Grade key={lado}>
                    <tr>
                      <Celula texto={lado} rotulo colunas={3} />
                    </tr>
                    <tr>
                      {LARGURA_OPCOES.map((o) => (
                        <Celula
                          key={o}
                          texto={o}
                          marcada={dados.difusoLargura[lado] === o}
                          aoClicar={() => alternaLado('difusoLargura', lado, o)}
                          somenteLeitura={sl}
                        />
                      ))}
                    </tr>
                  </Grade>
                ))}
              </div>
              <p className="oit__legenda">
                {LEGENDA_LARGURA.map((t) => (
                  <span key={t}>{t}</span>
                ))}
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* ---------------------------------------------------------- 4A */}
      <div className="oit__secao">
        <div className="oit__numero">4A</div>
        <div className="oit__corpo oit__pergunta">
          <span className="oit__enunciado">Outras anormalidades:</span>
          <SimNao
            valor={dados.outrasAnormalidades}
            aoMudar={(v) => def('outrasAnormalidades', v)}
            simRotulo="SIM (complete 4B)"
            naoRotulo="NÃO (finalizar a leitura)"
            somenteLeitura={sl}
          />
        </div>
      </div>

      {/* ---------------------------------------------------------- 4B */}
      <div className="oit__secao">
        <div className="oit__numero">4B</div>
        <div className="oit__corpo">
          <span className="oit__enunciado">Símbolos (vide legenda no verso):</span>
          <div style={{ marginTop: 3 }}>
            <Grade compacta>
              <tr>
                {SIMBOLOS.map((s) => (
                  <Celula
                    key={s}
                    texto={s === 'od' ? 'od*' : s}
                    marcada={dados.simbolos.includes(s)}
                    aoClicar={() => alternaSimbolo(s)}
                    somenteLeitura={sl}
                  />
                ))}
              </tr>
            </Grade>
          </div>
          <p className="oit__legenda">(*)OC: Necessário um comentário</p>
        </div>
      </div>

      {/* ---------------------------------------------------------- 4C */}
      <div className="oit__secao">
        <div className="oit__numero">4C</div>
        <div className="oit__corpo">
          <span className="oit__enunciado">Comentários:</span>
          <div className="oit__comentarios">
            {sl ? (
              <div className="oit__texto">{dados.comentarios}</div>
            ) : (
              <textarea
                value={dados.comentarios}
                onChange={(e) => def('comentarios', e.target.value)}
                rows={2}
              />
            )}
          </div>
        </div>
      </div>

      <div className="oit__assinatura">
        <span className="oit__assinatura-rotulo">Assinatura do leiturista responsável</span>
        {/* Mesma escala do cadastro do medico usada no laudo em texto: a
            assinatura e a mesma imagem, e sairia de dois tamanhos diferentes
            conforme o tipo de laudo. */}
        <div
          className="oit__assinatura-medico"
          style={{ '--assinatura-escala': (assinatura?.escala ?? 100) / 100 } as React.CSSProperties}
        >
          {assinatura?.url && <img src={assinatura.url} alt="" />}
          {assinatura && (
            <div className="oit__assinatura-nome">
              {assinatura.nome}
              <br />
              CRM:{assinatura.crm}
            </div>
          )}
        </div>
      </div>

      <div className="oit__rodape">{cabecalho.rodape}</div>
    </div>
  );
}
